import { isoToJalali } from '~/utils/jalali'
import { toFaDigits } from '~/utils/faDigits'

export type FaDateStyle = 'short' | 'long' | 'time'

/** Iran has no DST. A fixed offset keeps SSR and the browser on the same clock. */
const TEHRAN_OFFSET_MS = (3 * 60 + 30) * 60 * 1000

function tehranParts(date: Date) {
  const shifted = new Date(date.getTime() + TEHRAN_OFFSET_MS)
  const year = shifted.getUTCFullYear()
  const month = String(shifted.getUTCMonth() + 1).padStart(2, '0')
  const day = String(shifted.getUTCDate()).padStart(2, '0')
  const hours = String(shifted.getUTCHours()).padStart(2, '0')
  const minutes = String(shifted.getUTCMinutes()).padStart(2, '0')
  return {
    iso: `${year}-${month}-${day}`,
    hours,
    minutes,
  }
}

/**
 * Format an ISO/date string as Jalali (Persian calendar) with Persian digits.
 * `short` → ۱۴۰۵/۰۱/۰۱ — `long` → ۱۴۰۵/۰۱/۰۱، ۰۳:۳۰
 * Uses the Jalali converter, not `Intl`, so server and client render the same text.
 */
export function faDate(
  value: string | number | Date | null | undefined,
  style: FaDateStyle = 'short',
): string {
  if (value == null || value === '') return '—'
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  const { iso, hours, minutes } = tehranParts(date)
  const { jy, jm, jd } = isoToJalali(iso)
  const datePart = toFaDigits(`${jy}/${String(jm).padStart(2, '0')}/${String(jd).padStart(2, '0')}`)
  const timePart = toFaDigits(`${hours}:${minutes}`)
  if (style === 'time') return timePart
  if (style === 'long') return `${datePart}، ${timePart}`
  return datePart
}

/** Remaining whole days from now until `value` (negative if already past). */
export function remainingDays(value: string | number | Date | null | undefined): number | null {
  if (value == null || value === '') return null
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const ms = date.getTime() - Date.now()
  return Math.ceil(ms / 86_400_000)
}
